import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { IPublicLayout, IPublicProjectView } from "../../plots/types";
import tenantConfig from "config/tenant";
import LocationPicker from "shared/molecules/locationPicker/LocationPicker";
import { formatSqmFromSqft } from "utils/area";
import "./publicLayoutView.scss";

/** Shared media height for the sidebar cards — keeps the carousel and the map
 *  the same size so the two cards line up. Mirrored by `$plv-side-media`. */
const PLV_MAP_H = 240;
import {
  BRAND,
  PLOT_FILL,
  PLOT_FILL_HOVER,
  PLOT_STROKE,
  PLOT_TEXT,
  LEGEND_META,
} from "../../plots/colors";

const BASE_URL = process.env.REACT_APP_BASE_URL ?? "";

// De-dupe in-flight share-link requests by token. React 18 StrictMode mounts
// effects twice in dev, firing two GETs that race on the backend's share-link
// view-count write and surface as an optimistic-lock error ("Link
// unavailable"). Reusing one promise for both mounts keeps it to a single call.
const inFlightLayout = new Map<string, Promise<any>>();


/** Tray-with-arrow download glyph — used by the brochure action. */
const DownloadIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

// Bounding box of a polygon's point array.
function polyBBox(coords: { x: number; y: number }[]) {
  const xs = coords.map((p) => p.x);
  const ys = coords.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);
  return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY };
}

// ── Compass ───────────────────────────────────────────────────────────────────
// Fixed-size HTML badge (not part of the zoomable SVG) so it always renders as
// a clean north indicator in the top-right corner of the map.
const MapCompass: React.FC = () => (
  <div className="plv-map-compass" aria-label="North">
    <svg viewBox="0 0 40 40" width="34" height="34">
      <circle cx="20" cy="20" r="19" fill="#fff" stroke="#d1d5db" />
      {/* North needle (dark, points up) */}
      <polygon points="20,5 15,21 25,21" fill={BRAND.navy} />
      {/* South needle (light, points down) */}
      <polygon points="20,35 15,19 25,19" fill="#cbd5e1" />
      <text
        x="20"
        y="13.5"
        textAnchor="middle"
        fontSize="8"
        fontWeight={700}
        fill="#fff"
      >
        N
      </text>
    </svg>
  </div>
);

// ── Decorative tree (brochure-style greenery in the empty margins) ─────────────
const TreeGlyph: React.FC<{ x: number; y: number; r: number }> = ({
  x,
  y,
  r,
}) => (
  <g pointerEvents="none">
    {/* Cast shadow on the grass */}
    <ellipse
      cx={x + r * 0.18}
      cy={y + r * 1.05}
      rx={r * 0.62}
      ry={r * 0.18}
      fill="#3f5d34"
      opacity={0.22}
    />
    {/* Trunk */}
    <rect
      x={x - r * 0.07}
      y={y + r * 0.45}
      width={r * 0.14}
      height={r * 0.6}
      rx={r * 0.06}
      fill="#7a5a3a"
    />
    {/* Layered canopy with shaded gradient for depth */}
    <circle cx={x - r * 0.42} cy={y + r * 0.28} r={r * 0.5} fill="url(#plv-tree)" />
    <circle cx={x + r * 0.42} cy={y + r * 0.28} r={r * 0.5} fill="url(#plv-tree)" />
    <circle cx={x} cy={y + r * 0.42} r={r * 0.5} fill="url(#plv-tree)" />
    <circle cx={x - r * 0.22} cy={y - r * 0.08} r={r * 0.52} fill="url(#plv-tree)" />
    <circle cx={x + r * 0.24} cy={y - r * 0.04} r={r * 0.5} fill="url(#plv-tree)" />
    <circle cx={x} cy={y - r * 0.34} r={r * 0.46} fill="url(#plv-tree)" />
    {/* Subtle top-left highlight */}
    <circle cx={x - r * 0.3} cy={y - r * 0.3} r={r * 0.16} fill="#b6dd8e" opacity={0.5} />
  </g>
);

// ── Interactive layout canvas ─────────────────────────────────────────────────
interface SelectedPlot {
  id: string;
  plotNumber: string;
  status: string;
  areaSqft?: number;
  totalPrice?: number;
  lengthFt?: number;
  widthFt?: number;
  primaryFacing?: string;
  facingDirections?: string[];
  ratePerSqft?: number;
  cornerPlot?: boolean;
  parkFacing?: boolean;
}

// Matches the shared `.lc-plot-tip` card's max-width (280) so the flip logic
// never lets a fully-grown tooltip overflow the map edge.
const TOOLTIP_W = 280;
const TOOLTIP_H = 240;


const LayoutCanvas: React.FC<{
  layout: IPublicLayout;
  /** Rendered at the right of the card header, opposite the title. */
  headerActions?: React.ReactNode;
}> = ({ layout, headerActions }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Auto-generated layouts often place plots far outside the saved
  // (0,0,canvasWidth,canvasHeight) box, so the viewBox must follow wherever
  // the coordinates actually live — otherwise everything draws off-screen and
  // the canvas looks blank. Walk every drawn entity and build a padded box,
  // falling back to the saved canvas dimensions when no coords are present.
  const base = useMemo(() => {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    const acc = (coords?: { x: number; y: number }[]) => {
      if (!coords?.length) return;
      for (const p of coords) {
        if (p.x < minX) minX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.x > maxX) maxX = p.x;
        if (p.y > maxY) maxY = p.y;
      }
    };
    layout.plots?.forEach((p) => acc(p.coordinates));
    layout.roads?.forEach((r) =>
      acc(Array.isArray(r.coordinates) ? r.coordinates : r.coordinates ? [r.coordinates] : undefined),
    );
    layout.openSpaces?.forEach((s) => acc(s.coordinates));
    if (!Number.isFinite(minX) || maxX <= minX || maxY <= minY) {
      return {
        x: 0,
        y: 0,
        w: layout.canvasWidth || 1000,
        h: layout.canvasHeight || 1000,
      };
    }
    // Small percentage padding only (no large absolute floor) so the layout
    // fills the frame instead of floating tiny in a sea of green.
    const pad = Math.max((maxX - minX) * 0.05, (maxY - minY) * 0.05, 1);
    return {
      x: minX - pad,
      y: minY - pad,
      w: maxX - minX + pad * 2,
      h: maxY - minY + pad * 2,
    };
  }, [layout]);

  // The plot cluster here is portrait, but the map panel is wide/landscape — so
  // framing to the tight `base` leaves the layout tiny with huge green sides.
  // `frame` wraps the content in a border (room for the tree ring) and then
  // grows the short side to a landscape aspect, so the layout sits large and
  // centred and actually fills the panel.
  const frame = useMemo(() => {
    const bx = base.w * 0.14;
    const by = base.h * 0.14;
    let x = base.x - bx;
    let y = base.y - by;
    let w = base.w + bx * 2;
    let h = base.h + by * 2;
    const targetAspect = 1.55; // w : h
    if (w / h < targetAspect) {
      const nw = h * targetAspect;
      x -= (nw - w) / 2;
      w = nw;
    } else {
      const nh = w / targetAspect;
      y -= (nh - h) / 2;
      h = nh;
    }
    return { x, y, w, h };
  }, [base]);

  // The landscape `frame` leaves the portrait plot cluster reading small in the
  // middle of the panel, so the map opens one zoom step in (same 0.75 factor as
  // the + button) — the layout still fits entirely, just larger. Fit-all
  // returns here too, so the reset button restores this default view.
  const initialVb = useMemo(() => {
    const z = 0.75;
    return {
      x: frame.x + (frame.w * (1 - z)) / 2,
      y: frame.y + (frame.h * (1 - z)) / 2,
      w: frame.w * z,
      h: frame.h * z,
    };
  }, [frame]);

  const [vb, setVb] = useState(initialVb);
  const [hover, setHover] = useState<
    { plot: SelectedPlot; x: number; y: number } | null
  >(null);
  const dragRef = useRef<{
    sx: number;
    sy: number;
    ox: number;
    oy: number;
    moved: boolean;
  } | null>(null);

  const fsSm = Math.min(base.w, base.h) * 0.018;
  const fsMed = Math.min(base.w, base.h) * 0.026;
  // Road-name labels: tied to the larger span so they read clearly without
  // overpowering the plot numbers, matching the internal Layout tab.
  const roadFont = Math.max(Math.max(base.w, base.h) * 0.013, 0.8);
  // Width of the faux paved corridors between plots (grey underlay stroke).
  const corridorW = Math.min(base.w, base.h) * 0.03;

  // DWG coordinates are Y-up; SVG is Y-down. Flip every drawn Y about the
  // content's vertical extent so the share view matches the source drawing's
  // orientation (and the internal Layout tab) instead of rendering mirrored.
  const flipY = (y: number) => base.y * 2 + base.h - y;
  const flippedPoints = (coords: { x: number; y: number }[]) =>
    coords.map((p) => `${p.x},${flipY(p.y)}`).join(" ");

  // Per-corridor road tilt — ported from the internal Layout tab. A plot's
  // road-facing edge runs parallel to the road it fronts, so the nearest
  // plot-boundary edge to a road's anchor point gives that road its true local
  // tilt (the flat bottom road vs. the steep diagonal ones) instead of forcing
  // one site-wide angle. Computed in world (Y-up) coordinates, like the layout.
  const angleNearRoad = useMemo(() => {
    const edges: Array<[{ x: number; y: number }, { x: number; y: number }]> =
      [];
    layout.plots?.forEach((p) => {
      const poly = p.coordinates;
      if (!poly || poly.length < 2) return;
      for (let i = 0; i < poly.length; i++) {
        edges.push([poly[i], poly[(i + 1) % poly.length]]);
      }
    });
    return (x: number, y: number): number | null => {
      let best = Infinity;
      let bestDeg: number | null = null;
      for (const [a, b] of edges) {
        const vx = b.x - a.x;
        const vy = b.y - a.y;
        const len2 = vx * vx + vy * vy;
        if (len2 === 0) continue;
        let t = ((x - a.x) * vx + (y - a.y) * vy) / len2;
        t = Math.max(0, Math.min(1, t));
        const px = a.x + t * vx;
        const py = a.y + t * vy;
        const d = (x - px) ** 2 + (y - py) ** 2;
        if (d < best) {
          best = d;
          bestDeg = (Math.atan2(vy, vx) * 180) / Math.PI;
        }
      }
      // ≤ 7 CAD units: the anchor sits in/along a corridor lined by plots.
      return best <= 49 ? bestDeg : null;
    };
  }, [layout.plots]);

  // Surrounding farmland (शेतजमीन) — hedgerow trees scattered over the plain
  // tan backdrop. Purely cosmetic: they sit only where there's no plot/open-
  // space geometry, so they never cover or alter the actual layout.
  //
  // The tan field parcels that used to be drawn here are gone: their borders
  // painted a vertical/horizontal grid across the land around the DWG, which
  // read as part of the layout. The rows/cols below now only space the trees.
  const farmland = useMemo(() => {
    const pts: { x: number; y: number }[] = [];
    layout.plots?.forEach((p) => p.coordinates?.forEach((c) => pts.push(c)));
    layout.openSpaces?.forEach((s) =>
      (Array.isArray(s.coordinates) ? s.coordinates : []).forEach((c) =>
        pts.push(c),
      ),
    );
    const cols = 6;
    const rows = 5;
    const cw = frame.w / cols;
    const ch = frame.h / rows;

    // Hedgerow trees, spaced along the (now invisible) field lines.
    const minD = Math.min(base.w, base.h) * 0.14; // keep clear of the layout
    const spacing = Math.min(cw, ch) * 0.5;
    const treeR = Math.min(base.w, base.h) * 0.028;
    const far = (x: number, y: number) => {
      for (const q of pts) {
        if ((q.x - x) ** 2 + (q.y - y) ** 2 < minD * minD) return false;
      }
      return true;
    };
    const trees: Array<{ x: number; y: number; r: number }> = [];
    let k = 0;
    const push = (x: number, y: number) => {
      k++;
      if (far(x, y))
        trees.push({ x, y, r: treeR * (0.8 + (((k * 17) % 5) / 5) * 0.45) });
    };
    for (let i = 0; i <= cols; i++) {
      const lx = frame.x + i * cw;
      for (let y = frame.y; y <= frame.y + frame.h; y += spacing) {
        push(lx + (((++k * 53) % 7) / 7 - 0.5) * spacing * 0.3, y);
      }
    }
    for (let j = 0; j <= rows; j++) {
      const ly = frame.y + j * ch;
      for (let x = frame.x; x <= frame.x + frame.w; x += spacing) {
        push(x, ly + (((++k * 53) % 7) / 7 - 0.5) * spacing * 0.3);
      }
    }
    // Cap so it reads as tidy hedgerows, not a forest.
    const CAP = 150;
    const capped =
      trees.length > CAP
        ? trees.filter((_, i) => i % Math.ceil(trees.length / CAP) === 0)
        : trees;
    return { trees: capped };
  }, [layout, base, frame]);

  // Zoom is deliberately button-only (+ / − / fit). There is no wheel handler:
  // a trackpad two-finger scroll arrives as a wheel event, so wheel-zoom made
  // the map jump while the visitor was only trying to scroll the page past it.
  // Panning by drag stays available.

  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    dragRef.current = {
      sx: e.clientX,
      sy: e.clientY,
      ox: vb.x,
      oy: vb.y,
      moved: false,
    };
  };
  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!dragRef.current) return;
    const el = svgRef.current;
    /* istanbul ignore next -- ref is always set while mounted */
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const dx = ((e.clientX - dragRef.current.sx) / rect.width) * vb.w;
    const dy = ((e.clientY - dragRef.current.sy) / rect.height) * vb.h;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) dragRef.current.moved = true;
    setVb((prev) => ({
      ...prev,
      x: dragRef.current!.ox - dx,
      y: dragRef.current!.oy - dy,
    }));
  };
  const handleMouseUp = () => {
    dragRef.current = null;
  };

  const zoomIn = () =>
    setVb((v) => ({
      x: v.x + v.w * 0.125,
      y: v.y + v.h * 0.125,
      w: v.w * 0.75,
      h: v.h * 0.75,
    }));
  const zoomOut = () =>
    setVb((v) => ({
      x: v.x - (v.w * (1.33 - 1)) / 2,
      y: v.y - (v.h * (1.33 - 1)) / 2,
      w: Math.min(v.w * 1.33, frame.w * 4),
      h: Math.min(v.h * 1.33, frame.h * 4),
    }));
  const fitAll = () => setVb(initialVb);

  // Always show the core statuses (Available / Reserved / Booked / Unavailable)
  // in the legend even when no plots currently hold that status, then append any
  // other status that is actually present (e.g. Sold) so nothing is hidden.
  const CORE = ["AVAILABLE", "RESERVED", "BOOKED", "UNAVAILABLE"];
  const present = new Set(layout.plots.map((p) => p.status.toUpperCase()));
  const statuses = CORE.concat(
    Array.from(present).filter((s) => !CORE.includes(s)),
  ).filter((s) => LEGEND_META[s]);

  const hoveredId = hover?.plot.id ?? null;

  return (
    <div className="plv-canvas-wrap">
      <header className="plv-card__header">
        <h2 className="plv-card__title">
          <span className="plv-card__icon" aria-hidden="true">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="1 6 8 3 16 6 23 3 23 18 16 21 8 18 1 21 1 6" />
              <line x1="8" y1="3" x2="8" y2="18" />
              <line x1="16" y1="6" x2="16" y2="21" />
            </svg>
          </span>
          Project layout
        </h2>
        {headerActions}
      </header>

      <div className="plv-map-wrap" ref={wrapRef}>
        <svg
          ref={svgRef}
          viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
          width="100%"
          className="plv-map-svg"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => {
            handleMouseUp();
            setHover(null);
          }}
        >
          {/* Brochure-style grass backdrop: lighter in the middle, deeper at
              the edges, so the layout reads like a printed site plan. */}
          <defs>
            {/* Farmland (शेतजमीन) backdrop — warm tan, lighter in the middle. */}
            <radialGradient id="plv-grass" cx="50%" cy="45%" r="78%">
              <stop offset="0%" stopColor="#ece0a6" />
              <stop offset="55%" stopColor="#e0d291" />
              <stop offset="100%" stopColor="#cabb74" />
            </radialGradient>
            {/* Foliage shading: lit top-left, shadowed bottom-right, so the
                canopy reads with some depth instead of as a flat disc. */}
            <radialGradient id="plv-tree" cx="38%" cy="32%" r="72%">
              <stop offset="0%" stopColor="#9ccb74" />
              <stop offset="55%" stopColor="#6fa64f" />
              <stop offset="100%" stopColor="#4d7d38" />
            </radialGradient>
          </defs>
          <rect
            x={vb.x - 1000}
            y={vb.y - 1000}
            width={vb.w + 2000}
            height={vb.h + 2000}
            fill="url(#plv-grass)"
          />

          {/* Hedgerow trees over the plain tan backdrop. No field-parcel
              rectangles are drawn — their borders formed a grid of vertical and
              horizontal lines around the DWG. */}
          {farmland.trees.map((t, i) => (
            <TreeGlyph key={`tree-${i}`} x={t.x} y={flipY(t.y)} r={t.r} />
          ))}

          {/* Paved-road underlay — the API carries no corridor geometry, so we
              fake it: draw every plot with a thick grey stroke BEHIND the plot
              fills. Adjacent plots' strokes merge across the gaps, so the lanes
              between plot blocks read as paved roads (the cream plots on top
              leave only the gaps grey). */}
          <g>
            {layout.plots.map((p) =>
              p.coordinates?.length ? (
                <polygon
                  key={`road-base-${p.id}`}
                  points={flippedPoints(p.coordinates)}
                  fill="#c7c2b6"
                  stroke="#c7c2b6"
                  strokeWidth={corridorW}
                  strokeLinejoin="round"
                />
              ) : null,
            )}
          </g>

          {/* Open spaces — same treatment as the internal Layout tab: the plot
              palette (light fill, soft grey outline), never green, with the
              space name plus an "OPEN SPACE" subtitle in slate. */}
          {layout.openSpaces.map((os, i) => {
            const coords = Array.isArray(os.coordinates) ? os.coordinates : [];
            if (coords.length === 0) return null;
            const bb = polyBBox(coords);
            // Lead with the open-space number (19, 28) — the same label the
            // internal Layout tab shows — falling back to the space name with
            // its "(OPEN SPACE)" suffix stripped.
            const mainName =
              os.plotNumber?.trim() ||
              (os.spaceName ?? "")
                .replace(/\(?\s*open\s*space\s*\)?/i, "")
                .replace(/[()]/g, "")
                .trim();
            // Size the label to fit the polygon (small R.G. plots are tight).
            const f = Math.min(bb.h * 0.15, bb.w * 0.16, fsMed);
            const lx = os.labelX ?? bb.minX + bb.w / 2;
            const ly = flipY(os.labelY ?? bb.minY + bb.h / 2);
            return (
              <g key={os.id ?? `os-${i}`}>
                <polygon
                  points={flippedPoints(coords)}
                  fill={os.fillColor ?? "#f8fafc"}
                  fillOpacity={0.9}
                  stroke={os.fillColor ?? "#cbd5e1"}
                  strokeWidth={0.7}
                  strokeLinejoin="round"
                />
                <text
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontWeight={600}
                  fill="#475569"
                  pointerEvents="none"
                  style={{ userSelect: "none" }}
                >
                  {mainName && (
                    <tspan x={lx} y={ly - f * 0.45} fontSize={f}>
                      {mainName}
                    </tspan>
                  )}
                  <tspan
                    x={lx}
                    y={mainName ? ly + f * 0.6 : ly}
                    fontSize={mainName ? f * 0.6 : f}
                  >
                    OPEN SPACE
                  </tspan>
                </text>
              </g>
            );
          })}

          {/* Roads — the layout API sends each road as a single anchor point
              plus a name and CAD angle (no corridor polyline), so — like the
              internal Layout tab — we render the road name as an upright label
              tilted along its corridor. The corridors themselves read as the
              gaps between the plot blocks. */}
          {layout.roads.map((road, i) => {
            const c = road.coordinates;
            const pt = Array.isArray(c) ? c[0] : c;
            if (!pt || typeof pt.x !== "number" || typeof pt.y !== "number")
              return null;
            if (!road.roadName) return null;
            const rx = pt.x;
            const ry = flipY(pt.y);
            // Orient the label toward the drawing interior and keep it upright,
            // so long names grow inward instead of spilling off the plan edge.
            // Tilt: prefer the true per-corridor angle from nearby plot edges
            // (as the Layout tab does), falling back to the road's stored angle.
            const cX = base.x + base.w / 2;
            const cY = base.y + base.h / 2;
            const a0 = angleNearRoad(pt.x, pt.y) ?? road.angle ?? 0;
            const er = (a0 * Math.PI) / 180;
            let ex = Math.cos(er);
            let ey = Math.sin(er);
            if ((cX - pt.x) * ex + (cY - pt.y) * ey < 0) {
              ex = -ex;
              ey = -ey;
            }
            let deg = (Math.atan2(ey, ex) * 180) / Math.PI;
            let anchor: "start" | "end" = "start";
            if (deg > 90) {
              deg -= 180;
              anchor = "end";
            } else if (deg < -90) {
              deg += 180;
              anchor = "end";
            }
            return (
              <text
                key={road.id ?? `road-${i}`}
                x={rx}
                y={ry}
                textAnchor={anchor}
                dominantBaseline="middle"
                fontSize={roadFont}
                fontWeight={700}
                fill="#2563eb"
                stroke="#ffffff"
                strokeWidth={roadFont * 0.28}
                // SVG rotation is clockwise and Y is flipped, so negate the
                // CAD (CCW) angle to match the drawing's tilt.
                transform={`rotate(${-deg}, ${rx}, ${ry})`}
                pointerEvents="none"
                style={{ userSelect: "none", paintOrder: "stroke" }}
              >
                {road.roadName}
              </text>
            );
          })}

          {/* Plots */}
          {layout.plots.map((p) => {
            if (!p.coordinates?.length) return null;
            const status = p.status.toUpperCase();
            const isHovered = p.id === hoveredId;
            // Every status paints from the shared palette — AVAILABLE green,
            // RESERVED blue, BOOKED red — so the public masterplan reads the
            // same as the plot grid, the layout canvas and the legend below.
            const fill =
              (isHovered ? PLOT_FILL_HOVER[status] : PLOT_FILL[status]) ??
              "#f8fafc";
            const stroke = PLOT_STROKE[status] ?? "#cbd5e1";
            const sw = isHovered ? 0.45 : 0.3;
            // Place the label at the polygon centroid and size it to FIT the
            // plot — by height and by the label's character count — capped at a
            // single uniform size so big plots don't render bolder/larger than
            // small ones. No minimum, so labels never spill outside slivers.
            const pbb = polyBBox(p.coordinates);
            const cs = p.coordinates;
            const labelCx = cs.reduce((s, c) => s + c.x, 0) / cs.length;
            const labelCy = flipY(cs.reduce((s, c) => s + c.y, 0) / cs.length);
            const labelText = p.plotNumber;
            const labelFont = Math.min(
              fsSm,
              pbb.h * 0.6,
              pbb.w / (labelText.length * 0.62),
            );
            const pAny = p as any;
            const plotData: SelectedPlot = {
              id: p.id,
              plotNumber: p.plotNumber,
              status: p.status,
              areaSqft: p.areaSqft,
              totalPrice: pAny.totalPrice,
              lengthFt: pAny.lengthFt,
              widthFt: pAny.widthFt,
              primaryFacing: pAny.primaryFacing,
              facingDirections: pAny.facingDirections,
              ratePerSqft: pAny.ratePerSqft,
              cornerPlot: pAny.cornerPlot,
              parkFacing: pAny.parkFacing,
            };
            return (
              <g
                key={p.id}
                onMouseEnter={(e) => {
                  const rect = wrapRef.current?.getBoundingClientRect();
                  /* istanbul ignore next -- ref is always set while mounted */
                  if (!rect) return;
                  setHover({
                    plot: plotData,
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top,
                  });
                }}
                onMouseMove={(e) => {
                  if (!hover || hover.plot.id !== p.id) return;
                  const rect = wrapRef.current?.getBoundingClientRect();
                  /* istanbul ignore next -- ref is always set while mounted */
                  if (!rect) return;
                  setHover({
                    plot: plotData,
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top,
                  });
                }}
                onMouseLeave={() =>
                  setHover((h) => (h?.plot.id === p.id ? null : h))
                }
              >
                <polygon
                  points={flippedPoints(p.coordinates)}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={sw}
                  strokeLinejoin="round"
                />
                <text
                  x={labelCx}
                  y={labelCy}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={labelFont}
                  fontWeight={500}
                  fill={PLOT_TEXT[status] ?? "#64748b"}
                  pointerEvents="none"
                >
                  {labelText}
                </text>
              </g>
            );
          })}
        </svg>

        {/* North compass, pinned top-right. HTML rather than an SVG child so it
            stays a crisp, constant-size badge regardless of zoom / pan (inside
            the SVG it scaled and drifted over the plots). */}
        <div className="plv-map-topbar">
          <MapCompass />
        </div>

        {/* Hover tooltip (suppressed while panning) */}
        {hover && !dragRef.current?.moved && (() => {
          const wrap = wrapRef.current;
          // clientWidth/Height are always numeric while mounted; the `?? …`
          // fallbacks are unreachable defensive defaults.
          const W = wrap?.clientWidth ?? /* istanbul ignore next */ 800;
          const H = wrap?.clientHeight ?? /* istanbul ignore next */ 500;
          let left = hover.x + 16;
          let top = hover.y + 16;
          if (left + TOOLTIP_W > W) left = hover.x - TOOLTIP_W - 12;
          if (top + TOOLTIP_H > H) top = Math.max(0, hover.y - TOOLTIP_H - 12);
          return (
            <PlotTip plot={hover.plot} left={left} top={top} />
          );
        })()}

        <div className="plv-map-controls">
          <button className="plv-map-ctrl-btn" onClick={zoomIn} type="button">
            +
          </button>
          <button className="plv-map-ctrl-btn" onClick={zoomOut} type="button">
            −
          </button>
          <button
            className="plv-map-ctrl-btn"
            onClick={fitAll}
            type="button"
            title="Fit all"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
          </button>
        </div>
      </div>

      {/* Legend */}
      {statuses.length > 0 && (
        <div className="plv-legend">
          {statuses.map((s) => (
            <div key={s} className="plv-legend__item">
              <span
                className="plv-legend__dot"
                style={{ background: LEGEND_META[s]?.dot }}
              />
              <span className="plv-legend__label">{LEGEND_META[s]?.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Floating hover tooltip ───────────────────────────────────────────────────
const rupees = (v?: number | null) =>
  // Callers only pass positive numbers, so the "—" branch is defensive.
  /* istanbul ignore next */
  v == null ? "—" : `₹${Number(v).toLocaleString()}`;

const PlotTip: React.FC<{ plot: SelectedPlot; left: number; top: number }> = ({
  plot,
  left,
  top,
}) => {
  // Layout plots always carry a status, so the `?? ""` is a defensive guard.
  const statusKey = (plot.status ?? /* istanbul ignore next */ "").toUpperCase();
  const areaSqft = Number(plot.areaSqft ?? 0);
  const areaSqMtr = areaSqft > 0 ? formatSqmFromSqft(areaSqft) : null;
  // Shared `.lc-plot-tip` card (scss/components/_plotTooltip.scss) — the same
  // tooltip the plot grid and layout/DWG canvases use, so a plot reads
  // identically wherever it's hovered. Surface: 30% wash of the plot's own
  // status colour, matching LayoutCanvas/PlotList.
  const tipFill = PLOT_FILL[statusKey] ?? "#f8fafc";
  return (
    <div
      className="lc-plot-tip"
      style={{
        position: "absolute",
        left,
        top,
        pointerEvents: "none",
        ["--plot-tip-surface" as any]: `color-mix(in srgb, ${tipFill} 30%, #fff)`,
      }}
    >
      <div
        className={`lc-plot-tip__status lc-plot-tip__status--${statusKey.toLowerCase()}`}
      >
        {LEGEND_META[statusKey]?.label ?? plot.status}
      </div>
      <div className="lc-plot-tip__row">
        <span>Plot Number:</span>
        <strong>{plot.plotNumber}</strong>
      </div>
      {areaSqft > 0 && (
        <div className="lc-plot-tip__row">
          <span>Area:</span>
          <strong>
            {areaSqMtr} sq mtr ({areaSqft.toLocaleString()} sq ft)
          </strong>
        </div>
      )}
      {(plot.lengthFt || plot.widthFt) && (
        <div className="lc-plot-tip__row">
          <span>Size:</span>
          <strong>
            {plot.lengthFt ?? "—"} × {plot.widthFt ?? "—"} ft
          </strong>
        </div>
      )}
      {plot.primaryFacing && (
        <div className="lc-plot-tip__row">
          <span>Facing:</span>
          <strong>{plot.primaryFacing}</strong>
        </div>
      )}
      {Array.isArray(plot.facingDirections) &&
        plot.facingDirections.length > 0 && (
          <div className="lc-plot-tip__row">
            <span>Sides Open:</span>
            <strong>{plot.facingDirections.join(", ")}</strong>
          </div>
        )}
      {typeof plot.ratePerSqft === "number" && plot.ratePerSqft > 0 && (
        <div className="lc-plot-tip__row">
          <span>Rate / sqft:</span>
          <strong>{rupees(plot.ratePerSqft)}</strong>
        </div>
      )}
      {typeof plot.totalPrice === "number" && plot.totalPrice > 0 && (
        <div className="lc-plot-tip__row">
          <span>Total:</span>
          <strong>{rupees(plot.totalPrice)}</strong>
        </div>
      )}
      {plot.cornerPlot && <div className="lc-plot-tip__tag">Corner Plot</div>}
      {plot.parkFacing && <div className="lc-plot-tip__tag">Park Facing</div>}
    </div>
  );
};

// ── Media slider (cover images + brochures) ───────────────────────────────────
interface MediaItem {
  url: string;
  label: string;
}

const isImageUrl = (u: string): boolean =>
  /\.(jpg|jpeg|png|webp|gif|avif)(\?.*)?$/i.test(u);

// Forces a download. S3 objects are cross-origin, so a plain <a download> is
// ignored — we fetch the blob instead, and fall back to opening the URL if
// CORS blocks the fetch.
//
// The brochure is the page's only download, and it's served by an API endpoint
// whose last path segment is an opaque share token — so the caller always names
// the file and there's no URL-derived fallback to work out.
const downloadMedia = async (url: string, name_: string) => {
  let name = name_;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error("fetch failed");
    // Prefer the server's own filename when it sends one.
    const disposition = res.headers?.get?.("content-disposition");
    const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    if (match?.[1]) name = decodeURIComponent(match[1].trim());
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  } catch {
    window.open(url, "_blank", "noopener,noreferrer");
  }
};

/**
 * Copy to clipboard for the share link. Deliberately local rather than the
 * `projectDetailCopy` helper — that module drags rsuite's Whisper/IconButton
 * and the toaster into this public page's bundle for the sake of one function.
 */
const copyText = (text: string): Promise<void> => {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
  return new Promise((resolve, reject) => {
    const el = document.createElement("textarea");
    el.value = text;
    el.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0";
    document.body.appendChild(el);
    el.select();
    try {
      document.execCommand("copy");
      resolve();
    } catch (e) {
      reject(e);
    } finally {
      document.body.removeChild(el);
    }
  });
};

/**
 * Project location card.
 *
 * Mirrors the internal Project Details panel: the shared `LocationPicker` in
 * read-only mode, with "Project location" and the copy-link button handed to it
 * as its `label` (so they sit inline above the map), and the picker's own
 * Lat / Lng / address strip underneath.
 *
 * The card deliberately owns no map chrome of its own — no card header, no
 * coordinate overlay, no separate address tile — so the two screens render the
 * same component tree and can't drift apart.
 */
const ProjectLocationCard: React.FC<{
  latitude: number;
  longitude: number;
  address?: string;
}> = ({ latitude, longitude, address }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;

  const handleCopy = () => {
    copyText(shareUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        /* clipboard blocked — the coordinates are still on screen */
      });
  };

  return (
    <section className="plv-card plv-card--side plv-locate">
      {/* Not `resizable` like the internal panel: the picker's resize mode
          carries a 300px min-height, which would overrule PLV_MAP_H and leave
          this card taller than the carousel it's meant to line up with. */}
      <LocationPicker
        readOnly
        height={PLV_MAP_H}
        label={
          <span className="plv-locate__label">
            Project location
            <button
              type="button"
              className={
                "plv-locate__copy" +
                (copied ? " plv-locate__copy--copied" : "")
              }
              onClick={handleCopy}
              aria-label="Copy location link"
              title={copied ? "Copied!" : "Copy link"}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              {copied ? "Copied" : "Copy link"}
            </button>
          </span>
        }
        value={{ latitude, longitude, address }}
      />
    </section>
  );
};

/**
 * Full-screen media preview — the product-gallery pattern from Flipkart /
 * Amazon: click a cover image and it opens edge-to-edge over the dimmed page
 * with paging arrows, a thumbnail rail, a counter, and click-to-zoom that
 * magnifies around wherever you clicked. Closes on Esc, on the backdrop, or via
 * the X. Viewing only — covers are marketing imagery, not collateral to take
 * away, so the brochure is the page's single download.
 *
 * The sidebar card can only afford a 240px-tall thumbnail of what are usually
 * detailed site plans, so "click to see it properly" is the whole point of the
 * carousel — without this, the covers are decorative.
 */
const MediaLightbox: React.FC<{
  items: MediaItem[];
  index: number;
  onIndexChange: (i: number) => void;
  onClose: () => void;
}> = ({ items, index, onIndexChange, onClose }) => {
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");
  const safeIdx = Math.min(index, items.length - 1);
  const current = items[safeIdx];
  const isImg = isImageUrl(current.url);

  const len = items.length;

  // Esc closes and the arrow keys page — the keyboard contract every lightbox
  // is expected to honour. The page behind is frozen for the duration so a
  // scroll gesture over the overlay can't move the layout underneath.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onIndexChange((safeIdx - 1 + len) % len);
      else if (e.key === "ArrowRight") onIndexChange((safeIdx + 1) % len);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, onIndexChange, safeIdx, len]);

  // A new slide always starts un-zoomed, otherwise paging while zoomed drops
  // the viewer into an arbitrary corner of the next image.
  useEffect(() => {
    setZoomed(false);
    setOrigin("50% 50%");
  }, [safeIdx]);

  const go = (delta: number) =>
    onIndexChange((safeIdx + delta + items.length) % items.length);

  // Zoom about the click point, so tapping a plot number magnifies THAT plot
  // rather than the middle of the sheet.
  const toggleZoom = (e: React.MouseEvent<HTMLImageElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    setOrigin(
      `${((e.clientX - r.left) / r.width) * 100}% ${
        ((e.clientY - r.top) / r.height) * 100
      }%`,
    );
    setZoomed((z) => !z);
  };

  return (
    <div
      className="plv-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={current.label}
      // Backdrop-only dismiss: clicks that bubble up from the image, the
      // arrows or the rail must not close the viewer.
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="plv-lightbox__bar">
        <span className="plv-lightbox__title">{current.label}</span>
        <button
          type="button"
          className="plv-lightbox__close"
          onClick={onClose}
          aria-label="Close preview"
        >
          ×
        </button>
      </div>

      <div className="plv-lightbox__stage">
        {isImg ? (
          <img
            className={`plv-lightbox__img${
              zoomed ? " plv-lightbox__img--zoomed" : ""
            }`}
            style={zoomed ? { transformOrigin: origin } : undefined}
            src={current.url}
            alt={current.label}
            onClick={toggleZoom}
          />
        ) : (
          <iframe
            className="plv-lightbox__frame"
            src={current.url}
            title={current.label}
          />
        )}

        {items.length > 1 && (
          <>
            <button
              type="button"
              className="plv-lightbox__nav plv-lightbox__nav--prev"
              onClick={() => go(-1)}
              aria-label="Previous image"
            >
              ‹
            </button>
            <button
              type="button"
              className="plv-lightbox__nav plv-lightbox__nav--next"
              onClick={() => go(1)}
              aria-label="Next image"
            >
              ›
            </button>
          </>
        )}
      </div>

      <div className="plv-lightbox__footer">
        {items.length > 1 && (
          <div className="plv-lightbox__thumbs">
            {items.map((it, i) => (
              <button
                type="button"
                key={`${it.url}-${i}`}
                className={`plv-lightbox__thumb${
                  i === safeIdx ? " plv-lightbox__thumb--active" : ""
                }`}
                onClick={() => onIndexChange(i)}
                aria-label={`View ${it.label}`}
              >
                {isImageUrl(it.url) ? (
                  <img src={it.url} alt="" />
                ) : (
                  <span className="plv-lightbox__thumb-file">PDF</span>
                )}
              </button>
            ))}
          </div>
        )}
        <span className="plv-lightbox__count">
          {safeIdx + 1} / {items.length}
        </span>
      </div>
    </div>
  );
};

/**
 * Cover-images card: a titled carousel with overlaid arrows, an overlaid
 * counter and a thumbnail strip. Clicking the image opens `MediaLightbox`.
 */
const MediaCard: React.FC<{ items: MediaItem[]; title: string }> = ({
  items,
  title,
}) => {
  const [idx, setIdx] = useState(0);
  const [preview, setPreview] = useState(false);
  // Only rendered with a non-empty list, so this guard never fires.
  /* istanbul ignore next */
  if (items.length === 0) return null;

  const safeIdx = Math.min(idx, items.length - 1);
  const current = items[safeIdx];
  const isImg = isImageUrl(current.url);
  const go = (delta: number) =>
    setIdx((i) => (i + delta + items.length) % items.length);

  return (
    <section className="plv-card plv-card--side">
      <header className="plv-card__header">
        <h2 className="plv-card__title">
          <span className="plv-card__icon" aria-hidden="true">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
          </span>
          {title}
        </h2>
        {/* No action here — the cover is browse-only; clicking it opens the
            full-screen preview. */}
      </header>

      <div className="plv-slider">
        <div className="plv-slider__viewport">
        {isImg ? (
          <button
            type="button"
            className="plv-slider__open"
            onClick={() => setPreview(true)}
            aria-label={`Preview ${current.label}`}
          >
            <img
              className="plv-slider__img"
              src={current.url}
              alt={current.label}
            />
            {/* Affordance: without it, nothing says the thumbnail is
                clickable — it just looks like a static cover. */}
            <span className="plv-slider__open-hint" aria-hidden="true">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="7" />
                <line x1="20" y1="20" x2="16.2" y2="16.2" />
                <line x1="11" y1="8" x2="11" y2="14" />
                <line x1="8" y1="11" x2="14" y2="11" />
              </svg>
              Preview
            </span>
          </button>
        ) : (
          // PDFs: render inline via the browser's native PDF viewer so users
          // can flip pages without leaving the page. Falls back to a tap-to-
          // open caption-bar action via the download/external links.
          <iframe
            className="plv-slider__img"
            src={`${current.url}#toolbar=0&navpanes=0`}
            title={current.label}
          />
        )}

        {items.length > 1 && (
          <>
            <button
              type="button"
              className="plv-slider__nav plv-slider__nav--prev"
              onClick={() => go(-1)}
              aria-label="Previous"
            >
              ‹
            </button>
            <button
              type="button"
              className="plv-slider__nav plv-slider__nav--next"
              onClick={() => go(1)}
              aria-label="Next"
            >
              ›
            </button>
          </>
        )}

        {items.length > 1 && (
          <span className="plv-slider__count">
            {safeIdx + 1} / {items.length}
          </span>
        )}
      </div>

      {items.length > 1 && (
        <div className="plv-slider__thumbs">
          {items.map((it, i) => (
            <button
              type="button"
              key={`${it.url}-${i}`}
              className={`plv-slider__thumb${
                i === safeIdx ? " plv-slider__thumb--active" : ""
              }`}
              onClick={() => setIdx(i)}
              aria-label={`Go to ${it.label}`}
            >
              {isImageUrl(it.url) ? (
                <img src={it.url} alt={it.label} />
              ) : (
                <span className="plv-slider__thumb-file">PDF</span>
              )}
            </button>
          ))}
        </div>
      )}
      </div>

      {preview && (
        <MediaLightbox
          items={items}
          index={safeIdx}
          onIndexChange={setIdx}
          onClose={() => setPreview(false)}
        />
      )}
    </section>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const PublicLayoutView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<IPublicProjectView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const url = `${BASE_URL}/public/layout/project/${token}`;

    const run = (attempt: number): Promise<void> => {
      // Reuse an in-flight request (StrictMode double-mount) so we don't fire
      // two concurrent GETs that collide on the share-link write.
      const req = inFlightLayout.get(token) ?? axios.get(url);
      inFlightLayout.set(token, req);
      return req
        .then((res: any) => {
          if (cancelled) return;
          setData(res.data?.data ?? null);
          setError(null);
        })
        .catch((err: any) => {
          inFlightLayout.delete(token);
          const status = err?.response?.status as number | undefined;
          // Transient backend write-conflict (optimistic locking on the share
          // link) or a network blip — retry once before surfacing an error so
          // a momentary race doesn't show "Link unavailable".
          if (
            !cancelled &&
            attempt === 0 &&
            (status === undefined || status === 409 || status >= 500)
          ) {
            return new Promise<void>((r) => setTimeout(r, 450)).then(() =>
              run(1)
            );
          }
          if (!cancelled) {
            setError(
              err?.response?.data?.message ??
                "This link is invalid or has expired."
            );
          }
        });
    };

    setLoading(true);
    // Always evict the cached promise once it settles (success OR failure) so a
    // transient rejection is never left cached — a later mount refetches fresh.
    run(0).finally(() => {
      inFlightLayout.delete(token);
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const totalPlots = data?.layout?.plots?.length ?? data?.totalPlots ?? 0;
  const available =
    data?.layout?.plots?.filter((p) => p.status.toUpperCase() === "AVAILABLE")
      .length ?? 0;
  const booked =
    data?.layout?.plots?.filter((p) =>
      ["BOOKED", "SOLD"].includes(p.status.toUpperCase()),
    ).length ?? 0;
  const reserved =
    data?.layout?.plots?.filter((p) => p.status.toUpperCase() === "RESERVED")
      .length ?? 0;
  const unavailable =
    data?.layout?.plots?.filter((p) => p.status.toUpperCase() === "UNAVAILABLE")
      .length ?? 0;

  // Project name / city may arrive nested under `project` (current API) or as
  // legacy flat fields — read both.
  const projectName = data?.project?.name ?? data?.projectName ?? "";
  const projectLocation = Array.from(
    new Set(
      [
        data?.project?.taluka,
        data?.project?.city ?? data?.projectCity,
        data?.project?.state,
      ]
        .map((p) => p?.trim())
        .filter((p): p is string => !!p),
    ),
  ).join(", ");

  // Cover images are the only media surfaced on the share page — brochures are
  // sales collateral, not something a prospect browsing the plan needs, so the
  // payload's `brochureUrls` is intentionally left unrendered here.
  const coverItems = useMemo<MediaItem[]>(() => {
    const proj = data?.project;
    const covers = proj?.coverImageUrls?.length
      ? proj.coverImageUrls
      : [proj?.coverImageUrl ?? data?.coverImageUrl].filter(
          (u): u is string => !!u,
        );
    return covers.map((url, i) => ({ url, label: `Cover image ${i + 1}` }));
  }, [data]);

  // Site coordinates for the location card. Rendered only when the payload
  // carries both, so a project with no pinned location loses the card rather
  // than showing a map of the Atlantic.
  const geo = useMemo(() => {
    const proj = data?.project;
    const lat = Number(proj?.latitude);
    const lng = Number(proj?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat === 0 && lng === 0) return null;
    const address =
      [proj?.addressLine1, proj?.addressLine2, projectLocation, proj?.pincode]
        .map((p) => (typeof p === "string" ? p.trim() : ""))
        .filter(Boolean)
        .join(", ") || undefined;
    return { latitude: lat, longitude: lng, address };
  }, [data, projectLocation]);

  // The sidebar only earns its column when it has something to show.
  const hasSidebar = coverItems.length > 0 || !!geo;

  // Brochure download — served by the share-link API, not an S3 URL, so the
  // filename comes from the response's Content-Disposition rather than the
  // path (whose last segment is the opaque share token). Only offered when the
  // project actually has a brochure attached.
  const hasBrochure = !!(
    data?.project?.brochureUrls?.length || data?.project?.brochureUrl
  );
  const [downloadingBrochure, setDownloadingBrochure] = useState(false);
  const handleBrochureDownload = async () => {
    if (!token || downloadingBrochure) return;
    setDownloadingBrochure(true);
    try {
      await downloadMedia(
        `${BASE_URL}/public/layout/brochure-download/${token}`,
        `${projectName || "project"}-brochure.pdf`,
      );
    } finally {
      setDownloadingBrochure(false);
    }
  };

  return (
    <div className="plv">
      <header className="plv-header">
        <div className="plv-header__brand">
          {tenantConfig.publicHeaderLogo ? (
            <img
              src={tenantConfig.publicHeaderLogo}
              alt={tenantConfig.name}
              className="plv-header__logo plv-header__logo--wordmark"
            />
          ) : (
            <>
              <img
                src={tenantConfig.logoIcon}
                alt={tenantConfig.name}
                className="plv-header__logo"
              />
              <span className="plv-header__name">{tenantConfig.name}</span>
            </>
          )}
        </div>

        {/* Project identity lives in the header rather than in a banner over
            the layout: the page is a single 100vh screen, so a hero strip cost
            the plan ~90px of the only thing visitors came to look at. */}
        {data && projectName && (
          <div className="plv-header__project">
            <h1 className="plv-header__project-name" title={projectName}>
              {projectName}
            </h1>
            {projectLocation && (
              <span
                className="plv-header__project-city"
                title={projectLocation}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                {projectLocation}
              </span>
            )}
          </div>
        )}

        {data && (
          <div className="plv-header__stats">
            <span className="plv-header__stat">{totalPlots} Plots</span>
            <span className="plv-header__stat plv-header__stat--available">
              {available} Available
            </span>
            <span className="plv-header__stat plv-header__stat--booked">
              {booked} Booked
            </span>
            <span className="plv-header__stat plv-header__stat--reserved">
              {reserved} Reserved
            </span>
            <span className="plv-header__stat plv-header__stat--unavailable">
              {unavailable} Unavailable
            </span>
          </div>
        )}
      </header>

      <div className="plv-body">
        {loading && (
          <div className="plv-loading">
            <div className="plv-loading__spinner" />
            <p>Loading layout…</p>
          </div>
        )}

        {!loading && error && (
          <div className="plv-error">
            <div className="plv-error__icon">⚠️</div>
            <h2 className="plv-error__title">Link unavailable</h2>
            <p className="plv-error__msg">{error}</p>
          </div>
        )}

        {!loading && data && (
          <>
            {/* Dashboard grid: the layout is the hero and takes the wide
                column; cover images and location ride along in the sidebar, so
                all three read at a glance inside one screen. */}
            <div
              className={`plv-main${hasSidebar ? "" : " plv-main--full"}`}
            >
              <div className="plv-main__primary">
                <section className="plv-main__layout">
                  {(data.layout?.plots?.length ?? 0) === 0 ? (
                    <div className="plv-error plv-error--compact">
                      <div className="plv-error__icon">🗺️</div>
                      <h2 className="plv-error__title">Layout not available</h2>
                      <p className="plv-error__msg">
                        The plot layout for this project hasn't been published
                        yet.
                      </p>
                    </div>
                  ) : (
                    <LayoutCanvas
                      layout={data.layout}
                      // Brochure sits in the card header, on the title's
                      // baseline — the same slot every other card puts its
                      // action in. Floating over the map it competed with the
                      // plan for attention and covered plots at some pan
                      // positions.
                      headerActions={
                        hasBrochure ? (
                          <button
                            type="button"
                            className="plv-btn plv-btn--primary"
                            onClick={handleBrochureDownload}
                            disabled={downloadingBrochure}
                            aria-label="Download brochure"
                            title="Download brochure"
                          >
                            <DownloadIcon />
                            <span>
                              {downloadingBrochure
                                ? "Preparing…"
                                : "Download brochure"}
                            </span>
                          </button>
                        ) : undefined
                      }
                    />
                  )}
                </section>

              </div>

              {hasSidebar && (
                <aside className="plv-main__side">
                  {coverItems.length > 0 && (
                    <MediaCard items={coverItems} title="Cover images" />
                  )}
                  {geo && (
                    <ProjectLocationCard
                      latitude={geo.latitude}
                      longitude={geo.longitude}
                      address={geo.address}
                    />
                  )}
                </aside>
              )}
            </div>
          </>
        )}
      </div>

      <footer className="plv-footer">
        &copy; {new Date().getFullYear()} {tenantConfig.copyrightHolder} · All rights reserved
      </footer>
    </div>
  );
};

export default PublicLayoutView;
